import { Controller } from "stimulus"

// Counts consecutive "Your next steps" dismissals and announces when enough have
// stacked up to be worth asking why.
//
// Documented exception to docs/conventions/server-rendered-dom.md: the count is
// client-side state. The server cannot supply it — NextStepCardInteraction holds
// one current-state row per card and undo destroys the row, so a consecutive
// streak is not derivable from it. The count is transient and dies on
// navigate-away, which is the reset this feature specifies.
//
// Mounted outside #next-steps-frame: every dismissal replaces that frame, so a
// controller inside it would be rebuilt on each increment and never reach the
// threshold. Listeners are document-level for the same reason.
export default class extends Controller {
  // NOTE: Stimulus 2.0 value syntax — `name: Type` only; default applied in code.
  static values = { threshold: Number }

  connect() {
    this.streak = 0
    this.onSubmitEnd = (event) => this.handleSubmitEnd(event)
    this.onClick = (event) => this.handleClick(event)

    document.addEventListener("turbo:submit-end", this.onSubmitEnd)
    document.addEventListener("click", this.onClick)
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    document.removeEventListener("click", this.onClick)
  }

  get threshold() {
    return this.thresholdValue || 10
  }

  handleSubmitEnd(event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    // Only the goal picker counts as engagement. Resetting on every other form
    // costs the member a streak they were part-way through.
    if (form.dataset.nextStepsEngagement) return this.reset()

    // Marker set in _next_step_card.html.slim, and only there: undo posts to the
    // same endpoint with DELETE, so it falls through and leaves the count alone.
    if (!form.dataset.nextStepsDismissal) return

    // Submissions are serialized and an in-flight one aborts the next, dropping
    // its response. A dismissal the server never accepted left the card on
    // screen, so it is not a rejection to count.
    if (event.detail && event.detail.success === false) return

    this.increment()
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return
    if (event.target.closest(".next-step-card__body")) this.reset()
  }

  increment() {
    this.streak += 1
    if (this.streak < this.threshold) return

    // Reset rather than latch: the once-per-session gate belongs to the modal,
    // and a page left open long enough can legitimately earn a second streak.
    this.streak = 0
    this.element.dispatchEvent(new CustomEvent("next-steps-streak:reached"))
  }

  reset() {
    this.streak = 0
  }
}
